import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [businesses, setBusinesses] = useState([]);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    api.setToken(token);
    try {
      const userData = await api.getMe();
      setUser(userData);
      // Load businesses
      const bizList = await api.getBusinesses();
      setBusinesses(bizList);
      if (bizList.length > 0) {
        const saved = localStorage.getItem('activeBusiness');
        const found = bizList.find(b => b._id === saved);
        setActiveBusiness(found || bizList[0]);
      }
    } catch {
      localStorage.removeItem('token');
      api.setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    api.setToken(data.token);
    setUser(data.user);
    const bizList = await api.getBusinesses();
    setBusinesses(bizList);
    if (bizList.length > 0) {
      setActiveBusiness(bizList[0]);
    }
    return data;
  };

  const signup = async (email, password) => {
    const data = await api.signup(email, password);
    api.setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    api.setToken(null);
    setUser(null);
    setActiveBusiness(null);
    setBusinesses([]);
    localStorage.removeItem('activeBusiness');
  };

  const switchBusiness = (biz) => {
    setActiveBusiness(biz);
    localStorage.setItem('activeBusiness', biz._id);
  };

  const refreshBusinesses = async () => {
    const bizList = await api.getBusinesses();
    setBusinesses(bizList);
    if (!activeBusiness && bizList.length > 0) {
      setActiveBusiness(bizList[0]);
    }
    return bizList;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, signup, logout,
      activeBusiness, businesses, switchBusiness, refreshBusinesses,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
